import { Component, inject, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import {
  AllocationTypeKey,
  AllocationTypes,
} from '../../../models/token-allocation.model';
import { TokenTypeKey } from '../../../models/token-setting.model';
import { BillingService } from '../../services/billing.service';
import { allocationTypeLabel } from '../billing-labels';

type TokenTypeOption = {
  label: string;
  value: TokenTypeKey;
};

type AllocationTypeOption = {
  label: string;
  value: AllocationTypeKey.COMPLIMENTARY | AllocationTypeKey.CUSTOMER_PURCHASE;
};

@Component({
  selector: 'app-add-tokens-dialog',
  imports: [
    FormsModule,
    ButtonModule,
    DatePicker,
    DialogModule,
    InputNumberModule,
    SelectModule,
  ],
  templateUrl: './add-tokens-dialog.component.html',
})
export class AddTokensDialogComponent {
  private billingService = inject(BillingService);
  private messageService = inject(MessageService);

  visible = model(false);
  saved = output<void>();

  saving = false;

  readonly tokenTypeOptions: TokenTypeOption[] = [
    { label: 'Quote', value: TokenTypeKey.Quote },
    { label: 'CAD', value: TokenTypeKey.CAD },
  ];

  readonly allocationTypeOptions: AllocationTypeOption[] = [
    {
      label: AllocationTypes.COMPLIMENTARY.name,
      value: AllocationTypeKey.COMPLIMENTARY,
    },
    {
      label: AllocationTypes.CUSTOMER_PURCHASE.name,
      value: AllocationTypeKey.CUSTOMER_PURCHASE,
    },
  ];

  allocationType:
    | AllocationTypeKey.COMPLIMENTARY
    | AllocationTypeKey.CUSTOMER_PURCHASE
    | null = null;
  tokenType: TokenTypeKey | null = null;
  allocatedAmount: number | null = null;
  validFrom: Date | null = null;
  validTo: Date | null = null;

  get canSave(): boolean {
    return (
      this.allocationType !== null &&
      this.tokenType !== null &&
      this.allocatedAmount !== null &&
      this.allocatedAmount > 0 &&
      this.validFrom !== null &&
      this.validTo !== null &&
      this.validTo > this.validFrom
    );
  }

  onDialogShow(): void {
    this.resetForm();
  }

  close(): void {
    this.visible.set(false);
  }

  private resetForm(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const defaultEnd = new Date(today);
    defaultEnd.setDate(defaultEnd.getDate() + 30);

    this.allocationType = AllocationTypeKey.COMPLIMENTARY;
    this.tokenType = null;
    this.allocatedAmount = null;
    this.validFrom = today;
    this.validTo = defaultEnd;
    this.saving = false;
  }

  private toStartOfDayIso(date: Date): string {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized.toISOString();
  }

  private toEndOfDayIso(date: Date): string {
    const normalized = new Date(date);
    normalized.setHours(23, 59, 59, 999);
    return normalized.toISOString();
  }

  async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    this.saving = true;

    try {
      await this.billingService.createTokenAllocation({
        token_type: this.tokenType!,
        allocated_amount: this.allocatedAmount!,
        valid_from: this.toStartOfDayIso(this.validFrom!),
        valid_to: this.toEndOfDayIso(this.validTo!),
        allocation_type: this.allocationType!,
      });

      const typeLabel = allocationTypeLabel(this.allocationType!).toLowerCase();
      this.messageService.add({
        severity: 'success',
        summary: 'Tokens added',
        detail: `Added ${this.allocatedAmount} ${typeLabel} ${this.tokenTypeOptions.find((o) => o.value === this.tokenType)!.label} token(s).`,
      });
      this.saved.emit();
      this.visible.set(false);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Failed to add tokens',
        detail: 'Could not create the token allocation.',
      });
    } finally {
      this.saving = false;
    }
  }
}
